import type React from 'react';
import { useMemo, useState } from 'react';

import validate from '@vlabs/shared/validators';
import { ButtonSwitch, ButtonSwitchGroup, Control } from '@vlabs/uikit';
import { FileDropzone } from '@vlabs/uikit/components/controls/file-dropzone/FileDropzone';
import { merge } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BankCheckForm } from './bank-check/BankCheckForm';
import { cloneFileList } from './cloneFileList';
import { SUPPORTED_IMAGE_MIME_TYPES } from './constants';
import { ContentCheckForm } from './content-check/ContentCheckForm';
import { defaultBankValues, defaultContentValues } from './defaultValues';
import { DefaultCaption } from './DropzoneCaption';
import st from './ImageCheckForm.module.sass';
import type { TFormType, TImageCheckFormValues } from './types';

export type TImageCheckFormOnSubmit = (values: TImageCheckFormValues) => void;
export interface ImageCheckFormProps extends TImageCheckFormValues {
  onSubmit: TImageCheckFormOnSubmit;
  onCancel: VoidFunction;
}

const defaultValues: Record<TFormType, TImageCheckFormValues> = {
  bank: defaultBankValues,
  content: defaultContentValues,
};

const isFormFilled = (values: TImageCheckFormValues) => (
  (!!values.fileList && values.fileList.length > 0) && Object.entries(values).reduce(
    (prev, [fieldName, fieldValue]) => {
      if (![
        'liveness',
        'deepfake',
        'iso',
        'icao',
        'mindigital',
      ].includes(fieldName)) return prev;
      return prev || !!fieldValue;
    },
    false,
  ));

function ImageCheckForm({
  onSubmit,
  onCancel,
  ...formProps
}: ImageCheckFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const initialValues = merge({}, defaultContentValues, formProps);
  const { fileList } = initialValues;
  const [isSubmittable, setIsSubmittable] = useState<boolean>(isFormFilled(initialValues));
  const [selectedImage, setSelectedImage] = useState<File>();
  const [caption, setCaption] = useState(<DefaultCaption />);
  const form = useForm<TImageCheckFormValues>({ defaultValues: initialValues });

  const validateForm = (iData?: TImageCheckFormValues) => {
    const data = iData ?? form.getValues();
    const $isFormFilled = isFormFilled(data);
    if ($isFormFilled !== isSubmittable) {
      setIsSubmittable($isFormFilled);
    }
  };
  const formType = form.watch('type');

  form.watch((data, { name, type }) => {
    if (name === 'fileList' && type === 'change') {
      setSelectedImage(data.fileList?.[0]);
    }
    validateForm(data);
  });

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const values = form.getValues();
    if (!isFormFilled(values)) {
      validateForm(values);
      return;
    }

    onSubmit(values);
  };

  const DefaultImageRenderer = useMemo(() => {
    if (!selectedImage) return undefined;

    return <Control.ImageFileRenderer photo={URL.createObjectURL(selectedImage)} />;
  }, [selectedImage]);

  const TiffImageRenderer = useMemo(() => {
    if (!selectedImage) return undefined;

    return <Control.TiffFileRenderer photo={URL.createObjectURL(selectedImage)} />;
  }, [selectedImage]);

  const HeicImageRenderer = useMemo(() => {
    if (!selectedImage) return undefined;

    return <Control.HeicFileRenderer photo={URL.createObjectURL(selectedImage)} />;
  }, [selectedImage]);

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleFormSubmit}
      >
        <div
          className={st.Dropzone}
          onChangeCapture={(event) => {
            const input = event.target;
            if (!(input instanceof HTMLInputElement)) return;
            const files = Array.from(input.files ?? []);
            const file = files[0];
            if (!file || file.type || !file.name.toLowerCase().endsWith('.heic')) return;

            // Normalize before FileDropzone validates MIME and the form reads the input.
            files[0] = new File([file], file.name, {
              type: 'image/heic',
              lastModified: file.lastModified,
            });
            const dataTransfer = new DataTransfer();
            files.forEach((item) => dataTransfer.items.add(item));
            input.files = dataTransfer.files;
          }}
        >
          <h5 className={st.Name}>
            {t('checks:image checks.form.title')}
          </h5>
          <FileDropzone
            caption={caption}
            className={st.FileDropzone}
            data-testid="ImageCheckForm-FileDropzone"
            id="fileList"
            {...form.register('fileList', {
              required: validate.required(),
            })}
            defaultFile={fileList}
            fileViewer={Object.fromEntries(SUPPORTED_IMAGE_MIME_TYPES.map((mimeType) => {
              if (mimeType === 'image/tiff') return [mimeType, TiffImageRenderer];
              if (mimeType === 'image/heic') return [mimeType, HeicImageRenderer];
              return [mimeType, DefaultImageRenderer];
            }))}
            onChangeValue={(file) => {
              if (!file) setCaption(<DefaultCaption />);
              validateForm();
            }}
            onlyImages
            supportedImageTypes={SUPPORTED_IMAGE_MIME_TYPES}
          />

          <ButtonSwitchGroup <TFormType>
            className={st.TypeSwitch}
            onChange={(type) => {
              form.reset({
                ...defaultValues[type],
                type,
                fileList: cloneFileList(form.getValues('fileList')),
              });
            }}
            size="medium"
            value={formType}
            variant="square"
          >
            <ButtonSwitch value="content">{t('checks:image checks.form.type', { context: 'content' })}</ButtonSwitch>
            <ButtonSwitch value="bank">{t('checks:image checks.form.type', { context: 'bank' })}</ButtonSwitch>
          </ButtonSwitchGroup>

          {formType === 'bank' && <BankCheckForm />}
          {formType === 'content' && <ContentCheckForm />}

          <div className={st.Actions}>
            <Control.Button
              className={st.SubmitBtn}
              disabled={!isSubmittable}
              kind="primary"
              type="submit"
              variant="filled"
            >
              {t('checks:button.check')}
            </Control.Button>
            <Control.Button
              className={st.CancelBtn}
              kind="secondary"
              onClick={onCancel}
              variant="outlined"
            >
              {t('checks:button.cancel')}
            </Control.Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

export { ImageCheckForm };
